import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Chip from "../../Components/Chip";
import Stepper from "../../Components/Stepper";
import Button from "../../Components/Button";
import DishImage from "../../Components/DishImage";
import DishTag from "../../Components/DishTag";
import Price from "../../Components/Price";
import DiscountBadge from "../../Components/DiscountBadge";
import { runningDiscount, discountedLineTotal, cutsPrice } from "../../util/discount";
import { BackIcon, HeartIcon } from "../../Components/Icons";
import { NotesSection } from "./NotesSection";
import useFavorites from "../../hooks/useFavorites";
import useMenuData from "../../hooks/useMenuData";
import { useAppContext } from "../../context/AppContext";

/** The price of one dish with the extras the guest picked. */
export const unitPriceWithExtras = (item, chosenNames = []) => {
  const base = Number(item?.actual_price) || 0;
  const extras = (item?.options || [])
    .flatMap((group) => group?.values || [])
    .filter((value) => chosenNames.includes(value?.name))
    .reduce((sum, value) => sum + (Number(value?.price_delta) || 0), 0);
  return base + extras;
};

const OptionGroup = ({ title, note, children }) => (
  <section className="flex flex-col gap-2">
    <h2 className="text-sm font-bold text-dark">
      {title}
      {note && <span className="font-normal text-muted"> · {note}</span>}
    </h2>
    <div className="flex flex-wrap gap-2">{children}</div>
  </section>
);

const Index = ({
  closeModal,
  onAdded,
  handleSubmit,
  customizeSelectedItem: item,
  cookingLevel,
  setCookingLevel,
  removeIngredients,
  setRemoveIngredients,
  notes,
  setNotes,
  setQuantity,
  quantity,
}) => {
  const { t } = useTranslation();
  const { currencySymbol } = useAppContext();
  const { isFavorite, toggleFavorite } = useFavorites();
  const menuData = useMenuData();

  const single = item?.options?.find((o) => o.selection_type === "single_choice");
  const multi = item?.options?.find((o) => o.selection_type === "multi_choice");
  const maxExtras = multi?.selection_range?.max || 0;
  const name = item?.translations?.name;
  const tags = item?.productInfo?.features || [];
  const allergens = item?.productInfo?.allergens || [];
  const categoryName = useMemo(
    () =>
      menuData?.categories?.find((cat) =>
        (cat?.items || []).some((i) => i.menu_item_id === item?.menu_item_id)
      )?.translations?.name,
    [menuData, item]
  );

  const chosen = [...(cookingLevel || []), ...(removeIngredients || [])];
  const fullUnitPrice = unitPriceWithExtras(item, chosen);
  // The server applies the discount when the order is placed; this is the same estimate, shown early.
  const discount = runningDiscount(item);
  const discounted = Boolean(discount && cutsPrice(discount));
  const lineTotal = discounted ? discountedLineTotal(fullUnitPrice, quantity, discount) : fullUnitPrice * quantity;
  const unitPrice = quantity > 0 ? lineTotal / quantity : fullUnitPrice;
  const favorite = isFavorite(item?.menu_item_id);

  const toggleExtra = (valueName) =>
    setRemoveIngredients((prev) => {
      if (prev.includes(valueName)) return prev.filter((v) => v !== valueName);
      if (maxExtras > 0 && prev.length >= maxExtras) return prev;
      return [...prev, valueName];
    });

  const onAdd = useCallback(() => {
    // A new dish goes back to the menu with the dish, so the menu can show it landing in the order.
    const onClose = item?.edit
      ? closeModal
      : () =>
          onAdded({
            name: item?.translations?.name,
            image_url: item?.image_url,
            seed: item?.menu_item_id ?? item?.translations?.name,
          });
    handleSubmit(onClose, {
      ...item,
      quantity,
      unit_price: unitPrice,
      price: lineTotal,
    });
  }, [handleSubmit, closeModal, onAdded, item, quantity, unitPrice, lineTotal]);

  if (!item) return null;

  return (
    <div className="relative h-dvh flex flex-col bg-background">
      <header className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-3 pt-2">
        <button
          type="button"
          onClick={closeModal}
          aria-label={t("Back to Menu")}
          className="w-11 h-11 flex items-center justify-center rounded-full bg-surface/90 shadow-card text-dark"
        >
          <BackIcon />
        </button>
        <button
          type="button"
          onClick={() => toggleFavorite(item.menu_item_id)}
          aria-pressed={favorite ? "true" : "false"}
          aria-label={t("Favorites")}
          className={`w-11 h-11 flex items-center justify-center rounded-full bg-surface/90 shadow-card ${favorite ? "text-primary" : "text-dark"}`}
        >
          <HeartIcon filled={favorite} />
        </button>
      </header>

      <main className="flex-1 overflow-y-auto scrollbar-hide pb-6">
        <div className="h-[300px]">
          <DishImage fill src={item.image_url} alt={name || ""} seed={item.menu_item_id ?? name} size={216} />
        </div>

        <div className="px-6 flex flex-col gap-1.5 pt-4">
          <h1 className="text-xl font-bold text-dark">
            {name}
            {discount && <DiscountBadge discount={discount} className="ms-2 align-middle" />}
          </h1>
          {categoryName && <span className="text-sm text-muted">{categoryName}</span>}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {tags.map((tag) => (
                <DishTag key={tag}>{tag}</DishTag>
              ))}
            </div>
          )}
          {item.translations?.description && (
            <p className="text-sm text-muted pt-1">{item.translations.description}</p>
          )}
          {allergens.length > 0 && (
            <p className="text-sm text-muted">
              <span className="font-bold text-dark">{t("Allergens")}:</span> {allergens.join(", ")}
            </p>
          )}
        </div>

        <div className="px-6 flex flex-col gap-4 pt-4">
          {single && (
            <OptionGroup title={single.name || t("Cooking level")} note={t("Required")}>
              {single.values.map((value) => (
                <Chip
                  key={value.name}
                  selected={cookingLevel.includes(value.name)}
                  onClick={() => setCookingLevel([value.name])}
                  className="px-5"
                >
                  {value.name}
                </Chip>
              ))}
            </OptionGroup>
          )}

          {multi && (
            <OptionGroup
              title={multi.name || t("Extras")}
              note={maxExtras > 0 ? `${t("Up to")} ${maxExtras}` : null}
            >
              {multi.values.map((value) => {
                const on = removeIngredients.includes(value.name);
                const full = maxExtras > 0 && removeIngredients.length >= maxExtras && !on;
                return (
                  <Chip
                    key={value.name}
                    variant="outline"
                    selected={on}
                    disabled={full}
                    onClick={() => toggleExtra(value.name)}
                  >
                    <span className="flex items-center gap-1.5">
                      {value.name}
                      {value.price_delta > 0 && (
                        <span dir="ltr">+{currencySymbol}{value.price_delta}</span>
                      )}
                    </span>
                  </Chip>
                );
              })}
            </OptionGroup>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-dark">{t("Quantity")}</span>
            <Stepper
              value={quantity}
              onDecrease={() => setQuantity((q) => Math.max(1, q - 1))}
              onIncrease={() => setQuantity((q) => q + 1)}
            />
          </div>

          {(single || multi) && <NotesSection notes={notes} setNotes={setNotes} t={t} />}
        </div>
      </main>

      <div
        className="flex-none bg-background shadow-sheet px-6 pt-3.5 flex gap-3.5"
        style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}
      >
        <span className="h-cta min-w-24 px-3.5 rounded-lg bg-primary text-white shadow-brand flex items-center justify-center text-lg font-bold">
          {discounted && lineTotal !== fullUnitPrice * quantity ? (
            <span className="flex flex-col items-center leading-tight">
              <Price value={fullUnitPrice * quantity} className="text-xs line-through opacity-80" />
              <Price value={lineTotal} />
            </span>
          ) : (
            <Price value={lineTotal} />
          )}
        </span>
        <Button size="lg" className="flex-1" onClick={onAdd}>
          {t("Add to Order")}
        </Button>
      </div>
    </div>
  );
};

export default Index;
